'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface StatItem {
  id: string;
  value: string;
  label: string;
  imageUrl: string;
  imageAlt: string;
}

const stats: StatItem[] = [
{
  id: 'stat_homes',
  value: '1,200+',
  label: 'Homes Cleaned',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_10de60a26-1765801878220.png",
  imageAlt: 'Professional cleaner wiping kitchen counter with eco-friendly spray bottle'
},
{
  id: 'stat_rating',
  value: '4.9★',
  label: 'Average Rating',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_134d0fea5-1768259192462.png",
  imageAlt: 'Smiling cleaning team in green uniforms holding cleaning supplies in modern living room'
},
{
  id: 'stat_eco',
  value: '100%',
  label: 'Eco-Friendly Products',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_195ade0fa-1764775523622.png",
  imageAlt: 'Natural eco-friendly cleaning products in glass bottles on white marble counter'
}];


export default function HeroSection() {
  const [activeStat, setActiveStat] = useState<string>(stats[0].id);
  const [mainImage, setMainImage] = useState<string>(stats[0].imageUrl);
  const [mainImageAlt, setMainImageAlt] = useState<string>(stats[0].imageAlt);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleStatClick = (stat: StatItem) => {
    if (stat.id === activeStat || isTransitioning) return;

    setIsTransitioning(true);
    setActiveStat(stat.id);

    // Trigger fade out
    const imageEl = document.getElementById('main-hero-image');
    if (imageEl) {
      imageEl.classList.add('fade-out');
    }

    setTimeout(() => {
      setMainImage(stat.imageUrl);
      setMainImageAlt(stat.imageAlt);

      setTimeout(() => {
        if (imageEl) {
          imageEl.classList.remove('fade-out');
        }
        setIsTransitioning(false);
      }, 50);
    }, 500);
  };

  return (
    <section className="relative w-full min-h-screen overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-100 z-0" />
      
      {/* Beam Borders */}
      <div className="beam-border-h" />
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 2xl:px-24 py-10 sm:py-12 md:py-16 lg:py-32 xl:py-36 max-w-screen-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-8 xl:gap-12 items-center">
          
          {/* Left Column: Massive Typography */}
          <div className="lg:col-span-5 flex flex-col justify-center space-y-6 sm:space-y-8 reveal">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 border border-primary/20 bg-primary/5 rounded-full px-4 py-2 w-fit">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm font-medium text-primary uppercase tracking-wider">
                Next Available: Tomorrow 9 AM
              </span>
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[0.9] uppercase">
              Your Home.<br />
              Sparkling<br />
              Clean.<br />
              <span className="text-muted-foreground">In 2 Minutes.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed font-light">
              Professional cleaning services with eco-friendly products. Book online, pay securely, and enjoy a spotless home.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                href="/homepage#booking"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl active:scale-95 active:shadow-sm text-sm sm:text-base">
                
                Book Now
                <Icon name="CalendarIcon" size={18} variant="outline" />
              </Link>
              <Link
                href="/instant-quote"
                className="inline-flex items-center justify-center gap-2 border-2 border-border bg-card text-foreground px-5 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-muted transition-all active:scale-95 active:shadow-sm text-sm sm:text-base">
                
                Get Instant Quote
                <Icon name="CalculatorIcon" size={18} variant="outline" />
              </Link>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-4 sm:pt-6 border-t border-border">
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-primary" />
                <span className="text-xs sm:text-sm text-muted-foreground">Insured & Bonded</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-primary" />
                <span className="text-xs sm:text-sm text-muted-foreground">Background Checked</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="SparklesIcon" size={20} variant="solid" className="text-primary" />
                <span className="text-xs sm:text-sm text-muted-foreground">Eco-Friendly</span>
              </div>
            </div>
          </div>
          
          {/* Center Column: Floating Image */}
          <div className="lg:col-span-4 relative flex items-center justify-center py-4 sm:py-6 md:py-8 lg:py-0 reveal delay-200">
            {/* Decorative Plus Signs */}
            <div className="absolute top-1/4 right-0 text-muted-foreground/30 text-2xl font-light z-10">
              +
            </div>
            <div className="absolute bottom-10 left-0 text-muted-foreground/30 text-2xl font-light z-10">
              +
            </div>
            
            {/* Oval Image Container */}
            <div className="relative z-10 w-3/5 sm:w-3/4 md:w-2/3 lg:w-full xl:w-[90%] max-h-[280px] sm:max-h-[340px] md:max-h-[380px] lg:max-h-none xl:max-h-[600px] aspect-[3/4] rounded-[50%] overflow-hidden shadow-2xl border border-border group mx-auto">
              <AppImage
                id="main-hero-image"
                src={mainImage}
                alt={mainImageAlt}
                fill
                sizes="(max-width: 640px) 60vw, (max-width: 768px) 75vw, (max-width: 1024px) 67vw, (max-width: 1280px) 33vw, 400px"
                priority={false}
                quality={85}
                className="w-full h-full object-cover transition-all duration-700 ease-out" />
            </div>
          </div>
          
          {/* Right Column: Interactive Stats */}
          <div className="lg:col-span-3 flex flex-row lg:flex-col relative z-20 lg:pt-10 lg:pl-6 gap-4 md:gap-6 lg:gap-0 lg:space-y-12 reveal delay-400 justify-around lg:justify-start">
            {/* Vertical Beam Border - hidden on mobile */}
            <div className="beam-border-v hidden lg:block" />
            
            {stats.map((stat, index) =>
            <div
              key={stat.id}
              className={`stat-item ${activeStat === stat.id ? 'active' : ''} ${index !== 0 ? 'lg:border-t lg:border-border/50 lg:border-dashed lg:pt-12' : ''} flex-1 lg:flex-none text-center lg:text-left`}
              onClick={() => handleStatClick(stat)}>
              
                <span className="stat-value text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tighter text-muted-foreground block transition-colors">
                  {stat.value}
                </span>
                <span className="text-xs sm:text-sm text-muted-foreground uppercase tracking-widest mt-1 lg:mt-2 block lg:pl-2">
                  {stat.label}
                </span>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </section>);

}